import * as React from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import { useAgentTypeDropdown } from './useAgentTypeDropdown';
import {
  AGENT_TYPE_DROPDOWN_RESOURCE,
  AGENT_TYPE_NONE_LABEL,
  AGENT_TYPE_NONE_VALUE,
  AGENT_TYPE_OPTIONS,
} from './constants';

type AgentTypeDropdownProps = {
  className?: string;
  disabled?: boolean;
  /**
   * `header` (default) — dark-blue header look (white text + translucent border).
   * `sidebar` — theme-aware look (text.primary + theme divider border), full-width,
   * suitable for the AI Avatar sidebar.
   */
  variant?: 'header' | 'sidebar';
};

/**
 * Reusable dropdown to choose a global "agent type" filter.
 * Selecting a value writes it to localStorage so the HTTP layer can pick it
 * up and append `agent_type=<value>` as a query parameter to every API call.
 */
const AgentTypeDropdownComponent: React.FC<AgentTypeDropdownProps> = ({
  className,
  disabled,
  variant = 'header',
}) => {
  const { value, handleChange } = useAgentTypeDropdown();

  const headerSx = {
    color: 'white',
    minWidth: '220px',
    width: 'fit-content',
    marginLeft: '16px',
    height: '40px',
    '& .MuiSelect-icon': { color: 'white' },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.6)',
    },
  } as const;

  const sidebarSx = {
    color: 'text.primary',
    width: '100%',
    height: '40px',
    marginTop: 1,
    fontSize: '0.875rem',
    fontWeight: 500,
    '& .MuiSelect-icon': { color: 'text.secondary' },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'divider',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'text.secondary',
    },
  } as const;

  return (
    <Select
      className={className}
      labelId="agent-type-select-label"
      id="agent-type-select"
      displayEmpty
      disabled={disabled}
      value={value}
      onChange={handleChange}
      sx={variant === 'sidebar' ? sidebarSx : headerSx}
      renderValue={selected => {
        if (!selected) return AGENT_TYPE_NONE_LABEL;
        const match = AGENT_TYPE_OPTIONS.find(opt => opt.value === selected);
        return match ? match.label : AGENT_TYPE_NONE_LABEL;
      }}
    >
      <MenuItem value={AGENT_TYPE_NONE_VALUE}>{AGENT_TYPE_NONE_LABEL}</MenuItem>
      {AGENT_TYPE_OPTIONS.map(option => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export const AgentTypeDropdown = withResourceProtection(
  AgentTypeDropdownComponent,
  AGENT_TYPE_DROPDOWN_RESOURCE,
  null, // fallback: render nothing when the user lacks the resource
);

export default AgentTypeDropdown;
